// Drives the Google Places provider search on the SCA case form
// (partners/provider_access_cases/new). Google Places is the form's only
// provider-search surface.
//
// Flow:
//   1. Debounced search  -> POST (body, never query string) -> results turbo stream
//   2. Click a place     -> compact site summary, POST place_id ->
//      server resolves candidates -> turbo stream. Change returns to search.
//   3. Click a candidate card -> its signed token is copied into the hidden
//      candidate_token field. The "Other" card also expands NPI/EIN inputs.
//   4. Enter what the visit is for and what the issue is, then explicitly
//      click Continue. The button requires a member, billing identity, and
//      nonblank visit and issue descriptions.
//
// The server enforces every guard (min length, capped results, resolution,
// token signing/verification); this controller is purely presentational.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "candidates", "submit", "manualFields", "token", "siteSearch", "manualSite", "saveSite", "siteSummary", "siteName", "siteAddress", "siteSnapshot", "otherReporter"];
  static values = {
    searchUrl: String,
    selectUrl: String,
    manualSiteUrl: String,
    requireIssue: { type: Boolean, default: true },
    keepBillingChoicesVisible: { type: Boolean, default: false },
    minLength: { type: Number, default: 3 },
    wait: { type: Number, default: 300 },
    // Mirrors _provider_result_card's `selected:` styling for a client-side
    // pick; toggled on the clicked card wrapper.
    selectedClasses: { type: String, default: "border-core-purple-500 bg-[#FCFCFF] shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]" },
    unselectedClasses: { type: String, default: "border-support-light-300 hover:border-support-light-500 hover:shadow-[0_4px_16px_rgba(45,48,71,0.07)]" },
    // Toggled on the search input while a Google search is in flight (the same
    // shimmer the Provider Intelligence Add Provider drawer uses).
    busyClass: { type: String, default: "" },
  };

  connect() {
    this.timeout = null;
    this.searchController = null;
    this.selectController = null;
    this.googleActive = false;
    // Bumped every time the query changes or a local search/selection takes
    // ownership. A response captures the generation it was dispatched under and
    // refuses to render if it is now stale — a belt-and-suspenders guard against
    // an out-of-order completion slipping past the AbortController.
    this.generation = 0;
    this.adoptPreselectedCandidate();
  }

  // A validation re-render (e.g. a missing member) arrives with the verified
  // Google selection already chosen: the hidden token plus, for a manual-entry
  // candidate, whatever NPI/EIN the operator had already typed. Adopt it so
  // Continue reflects it and a new search still clears it correctly.
  adoptPreselectedCandidate() {
    const preserved = this.element.querySelector("[data-testid='sca-google-places-preserved']");
    if (preserved?.querySelector("input[type='hidden'][name='candidate_token']")) {
      this.googleActive = true;
    }
    // The server already rendered each "I don't know" link, its hidden input
    // and its identifier in the right state; re-assert it so they can't drift.
    this.applyUnknownState();
    this.updateSubmit();
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.searchController) this.searchController.abort();
    if (this.selectController) this.selectController.abort();
  }

  // ---- Search -----------------------------------------------------------

  onSearchInput() {
    // Starting a Google search transfers ownership away from any previously
    // selected Goodbill record immediately. This clears its hidden scope/id and
    // disables submit during the debounce, so Enter cannot submit a stale local
    // provider while the operator is visibly switching search paths.
    this.dispatch("selected");
    // Typing a new query voids any prior Google selection AND invalidates any
    // in-flight place-details request so a late candidates response can't
    // repopulate the region under a query the operator has already changed.
    this.generation += 1;
    this.resetGoogleSelection();
    // Remove old candidate cards immediately, not after the debounce: otherwise
    // an operator can click and submit a candidate from the previous query.
    this.clearCandidates();
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.runSearch(), this.waitValue);
  }

  runSearch() {
    const query = this.inputTarget.value;
    const trimmed = query.trim();
    // Below the floor (including an emptied box) we make no request and clear any
    // stale results, candidates, and Google selection so old matches can neither
    // linger on screen nor be submitted. The server enforces the same minimum.
    if (trimmed.length < this.minLengthValue) {
      this.abortSearch();
      this.abortSelect();
      // The aborted search's finally() sees a newer generation and leaves the
      // shimmer alone, so the owner of this generation must clear it here.
      this.setBusy(false);
      this.clearResults();
      this.clearCandidates();
      this.resetGoogleSelection();
      return;
    }

    // A new valid search supersedes both any prior search AND any in-flight
    // place-details request — the candidates it would render belong to a place
    // the operator is no longer looking at.
    this.abortSearch();
    this.abortSelect();
    this.searchController = new AbortController();
    const generation = this.generation;
    this.clearCandidates();
    // No loading row: the in-flight state is the shimmer on the input itself.
    this.setBusy(true);

    this.postTurboStream(
      this.searchUrlValue,
      { place_query: query, member_id: this.memberId() },
      this.searchController.signal,
      // A hard fetch/transport failure renders the same "unavailable" state the
      // server sends on the happy path, never a hung loading row or a dead box.
      () => this.showError(this.resultsTarget, "Provider search is temporarily unavailable. Please try again.", "sca-google-places-error"),
      () => generation === this.generation,
    ).finally(() => {
      // A superseded search leaves the shimmer to the request that now owns it.
      if (generation === this.generation) this.setBusy(false);
    });
  }

  // ---- Select a place ---------------------------------------------------

  selectPlace(event) {
    if (this.nestedControlClick(event)) return;
    this.cancelLabelActivation(event);
    const card = event.currentTarget;
    const placeId = card.dataset.placeId;
    if (!placeId) return;

    // A result can still be clicked during the next query's debounce. The
    // selection now owns the UI, so that queued search must not abort it.
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.setBusy(false);

    // Clicking the selected place again deselects it: the other results come
    // back and the billing-identity region (and any in-flight lookup) is dropped.
    if (this.isSelectedCard(card)) {
      this.generation += 1;
      this.abortSelect();
      this.markSelectedCard(null, this.placeCards());
      this.clearCandidates();
      this.resetGoogleSelection();
      return;
    }

    // The chosen place card stays on screen in its selected state and the
    // other results collapse; only the billing-identity region below reloads.
    this.generation += 1;
    this.markSelectedCard(card, this.placeCards());
    this.showSite(card.dataset);
    this.resetGoogleSelection();
    this.abortSelect();
    this.selectController = new AbortController();
    const generation = this.generation;
    this.showLoading(this.candidatesTarget, "Matching a billing NPI and EIN…");

    this.postTurboStream(
      this.selectUrlValue,
      { place_id: placeId, member_id: this.memberId() },
      this.selectController.signal,
      // Same guarantee as search: a transport failure surfaces the unavailable
      // state in the candidates region instead of leaving the loader spinning.
      () => this.showError(this.candidatesTarget, "Provider lookup is temporarily unavailable. Please try again.", "sca-google-places-candidates-error"),
      // Never repopulate candidates for a place the operator has moved on from
      // (query changed, or a local search/selection took ownership mid-flight).
      () => generation === this.generation,
    );
  }

  // ---- Choose a candidate ------------------------------------------------

  // Selects a billing identity: the card's signed token becomes the form's
  // candidate_token (enabled so Turbo includes it on submit), the card is
  // highlighted while the other choices stay available. "Other" expands NPI/EIN
  // inputs. Both paths wait for Continue; clicking a selected card deselects.
  chooseCandidate(event) {
    if (this.nestedControlClick(event)) return;
    this.cancelLabelActivation(event);
    const card = event.currentTarget;
    const { token, manual } = card.dataset;
    if (!token || !this.hasTokenTarget) return;

    if (this.isSelectedCard(card)) {
      this.resetGoogleSelection();
      return;
    }

    this.googleActive = true;
    // Hand off to the local (access-issue-form) controller so it clears its own
    // hidden scope/id AND its visible selected card/details — the two paths must
    // never both look or submit as selected. Fires before we (re-)enable submit
    // because the local controller's clear disables it as a side effect.
    this.dispatch("selected");
    this.tokenTarget.value = token;
    this.tokenTarget.disabled = false;
    this.markSelectedCard(card, this.candidateCards(), !this.keepBillingChoicesVisibleValue);
    if (manual === "true") {
      this.showManualEntry();
      this.updateSubmit();
    } else {
      this.hideManualEntry();
      this.updateSubmit();
    }
  }

  chooseManualCandidate() {
    this.googleActive = true;
    this.dispatch("selected");
    this.updateSubmit();
  }

  // "I don't know" for one identifier (partner Create access issue only; the
  // provider cash-pay form renders the same card without these links and keeps
  // both required). The link flips its identifier's hidden `_unknown` input,
  // which is the single source of this state; marking one unknown clears and
  // disables its input and drops its `required` so the browser stops demanding
  // a value, and Continue then treats that half of the pair as answered. The
  // link becomes "Enter NPI"/"Enter EIN", which undoes it. Either or both may
  // be unknown.
  toggleUnknown(event) {
    const field = this.unknownField(event.currentTarget.dataset.unknownToggleFor);
    if (!field) return;

    const nowUnknown = field.dataset.unknown !== "true";
    field.dataset.unknown = nowUnknown ? "true" : "false";
    this.applyUnknownState();
    // Going back to entering it should land the cursor in the input that just
    // came back, not leave the operator on the link they clicked.
    if (!nowUnknown) this.manualFieldsTarget.querySelector(`[name='${field.dataset.unknownFor}']`)?.focus();
    this.chooseManualCandidate();
  }

  // Re-asserts each identifier's unknown answer against its input, its link and
  // its hidden input. Called after the blanket enable/disable in
  // show/hideManualEntry, which would otherwise re-enable an input the operator
  // has already said they can't fill in — and, worse, enable (so submit) a
  // hidden "unknown" input for an identifier nobody marked unknown.
  applyUnknownState() {
    const manualActive = this.manualEntryActive();
    this.unknownToggles().forEach((field) => {
      const name = field.dataset.unknownFor;
      const unknown = field.dataset.unknown === "true";
      field.disabled = !(unknown && manualActive);
      this.unknownLinks(name).forEach((link) => {
        link.textContent = unknown ? link.dataset.labelUndo : link.dataset.labelMark;
        link.setAttribute("aria-label", unknown ? link.dataset.ariaUndo : link.dataset.ariaMark);
        link.disabled = !manualActive;
      });
      const input = this.manualFieldsTarget.querySelector(`[name='${name}']`);
      if (!input) return;
      if (unknown) {
        input.value = "";
        input.removeAttribute("required");
        input.disabled = true;
        // Mirrors the placeholder _google_places_other_card renders server-side.
        input.placeholder = "Unknown";
      } else {
        input.setAttribute("required", "required");
        input.disabled = !manualActive;
        input.placeholder = input.dataset.normalPlaceholder || "";
      }
    });
  }

  unknownToggles() {
    if (!this.hasManualFieldsTarget || !this.manualFieldsTarget) return [];

    return Array.from(this.manualFieldsTarget.querySelectorAll("[data-unknown-for]"));
  }

  unknownField(name) {
    return this.unknownToggles().find((field) => field.dataset.unknownFor === name);
  }

  unknownLinks(name) {
    if (!this.hasManualFieldsTarget || !this.manualFieldsTarget) return [];

    return Array.from(this.manualFieldsTarget.querySelectorAll(`[data-unknown-toggle-for='${name}']`));
  }

  identifierUnknown(name) {
    return this.unknownField(name)?.dataset.unknown === "true";
  }

  // Each card's checkbox is a visually hidden <input> wrapped in a <label>
  // (shared/checkbox). Clicking the visible box hits the label, and the browser
  // then fires a second, synthetic click on the <input>. That click bubbles back
  // to the card, so the pick toggled on and straight back off: the box looked
  // unclickable and the place's billing identities never loaded. Cancel the
  // label's activation and let markSelectedCard set the checkbox. A click whose
  // target is the input itself (keyboard Space) is left alone, so it still
  // toggles exactly once.
  cancelLabelActivation(event) {
    const target = event?.target;
    if (!target?.closest || target.matches?.("input")) return;
    const label = target.closest(".gb-checkbox");
    if (label && event.currentTarget?.contains?.(label)) event.preventDefault();
  }

  // A button or link inside a card (e.g. the billing identity card's
  // "Show all N" associated-NPI toggle) does its own thing and must not also
  // pick or unpick the card.
  nestedControlClick(event) {
    const control = event?.target?.closest?.("button, a, select, textarea");
    return Boolean(control && event.currentTarget?.contains?.(control));
  }

  // Clicks inside the Other card's inputs must not toggle the card itself.
  stopPropagation(event) {
    event.stopPropagation();
  }

  siteSnapshotTargetConnected(snapshot) {
    this.showSite(snapshot.dataset);
    this.updateSubmit();
  }

  tokenTargetConnected() { this.updateSubmit(); }

  showSite({ name, address }) {
    if (!this.hasSiteSummaryTarget) return;
    this.siteSearchTarget.classList.add("hidden");
    this.setManualSiteVisible(false);
    this.siteSummaryTarget.classList.remove("hidden");
    this.siteSummaryTarget.classList.add("flex");
    this.siteNameTarget.textContent = name || "Selected site of care";
    this.siteAddressTarget.textContent = address || "";
  }

  changeSite({ resumeSearch = true } = {}) {
    this.clearForLocalSelection();
    this.manualSitePending = false;
    this.setBusy(false);
    this.setManualSiteVisible(false);
    this.siteSummaryTarget.classList.add("hidden");
    this.siteSummaryTarget.classList.remove("flex");
    this.siteSearchTarget.classList.remove("hidden");
    this.inputTarget.focus();
    this.updateSubmit();
    if (resumeSearch) this.runSearch();
  }

  setManualSiteVisible(visible) {
    if (!this.hasManualSiteTarget) return;
    this.manualSiteTarget.classList.toggle("hidden", !visible);
    this.manualSiteTarget.querySelectorAll("input").forEach(input => {
      // Retain entered text in form submissions so an expired token can return
      // it for re-confirmation. Only the signed token establishes the site.
      input.disabled = !visible && !input.value;
      input.required = visible;
    });
  }

  enterManualSite() {
    this.changeSite({ resumeSearch: false });
    this.siteSearchTarget.classList.add("hidden");
    this.setManualSiteVisible(true);
    const name = this.element.querySelector("[name='site_name']");
    if (!name.value) name.value = this.inputTarget.value.trim();
    name.focus();
    this.updateSubmit();
  }

  selectManualSite() {
    if (this.saveSiteTarget.disabled) return;
    this.generation += 1;
    this.abortSelect();
    this.selectController = new AbortController();
    const generation = this.generation;
    this.manualSitePending = true;
    this.updateSubmit();
    this.showLoading(this.candidatesTarget, "Saving site of care…");
    this.postTurboStream(
      this.manualSiteUrlValue,
      { member_id: this.memberId(), site_name: this.element.querySelector("[name='site_name']").value, site_address: this.element.querySelector("[name='site_address']").value },
      this.selectController.signal,
      () => this.showError(this.candidatesTarget, "Unable to save this site of care. Check the name and address and try again.", "sca-manual-site-error"),
      () => generation === this.generation,
      true,
    ).finally(() => {
      if (generation === this.generation) {
        this.manualSitePending = false;
        this.updateSubmit();
      }
    });
  }

  // Refresh the page-level Continue button as any required field changes.
  updateSubmit(event) {
    if (this.manualSitePending && event?.type === "input" && this.manualSiteTarget.contains(event.target)) {
      this.generation += 1;
      this.abortSelect();
      this.manualSitePending = false;
      this.clearCandidates();
    }
    const other = this.element.querySelector("[name='heard_from']:checked")?.value === "other";
    const reporter = this.element.querySelector("[name='heard_from_other']");
    if (this.hasOtherReporterTarget) {
      this.otherReporterTarget.classList.toggle("hidden", !other);
      reporter.required = other;
    }
    if (this.hasSaveSiteTarget) {
      const name = this.element.querySelector("[name='site_name']").value.trim();
      const address = this.element.querySelector("[name='site_address']").value.trim();
      this.saveSiteTarget.disabled = Boolean(this.manualSitePending) || !this.memberId() || !name || name.length > 200 || !address || address.length > 500;
    }
    if (this.hasSubmitTarget) {
      const reason = this.element.querySelector("[name='reason_for_visit']")?.value.trim();
      const issue = this.element.querySelector("[name='issue_description']")?.value.trim();
      this.submitTarget.disabled = !(this.memberId() && this.billingIdentityPresent() && reason && (!this.requireIssueValue || issue) && (!other || reporter?.value.trim()));
    }
  }

  manualEntryActive() {
    return this.hasManualFieldsTarget && !this.manualFieldsTarget.classList.contains("hidden");
  }

  billingIdentityPresent() {
    if (!this.hasTokenTarget || this.tokenTarget.disabled || !this.tokenTarget.value) return false;
    if (!this.manualEntryActive()) return true;
    return this.manualIdentifierReady("manual_provider_npi", 10) && this.manualIdentifierReady("manual_provider_ein", 9);
  }

  // An identifier is ready when it has the right number of digits OR the
  // operator said "I don't know" for it — an honest unknown is an answer, so
  // Continue must not stay disabled waiting for digits that don't exist.
  manualIdentifierReady(name, digits) {
    if (this.identifierUnknown(name)) return true;

    const value = this.manualFieldsTarget.querySelector(`[name='${name}']`)?.value ?? "";
    return value.replace(/\D/g, "").length === digits;
  }

  // Called from the local (Goodbill records) search box so the two selection
  // paths never both submit. Only disables the button if Google owned it. Also
  // takes ownership away from any in-flight Google search/select so a late
  // response can't repopulate results or candidates once the operator has
  // switched to the local box.
  onLocalSearchInput() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
  }

  // Invoked when the local (access-issue-form) controller commits a selection
  // (dispatches access-issue-form:selected). A committed local pick fully
  // supersedes Google: drop the token/banner and wipe the results + candidate
  // regions so nothing Google-side lingers on screen or in the submission.
  clearForLocalSelection() {
    this.generation += 1;
    clearTimeout(this.timeout);
    this.timeout = null;
    this.abortSearch();
    this.abortSelect();
    this.resetGoogleSelection();
    this.clearResults();
    this.clearCandidates();
  }

  // ---- Helpers ----------------------------------------------------------

  async postTurboStream(url, params, signal, onFailure, shouldRender = null, allowValidationErrors = false) {
    const body = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") body.append(key, value);
    });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: body.toString(),
        signal,
      });
      // A superseded-but-unaborted response (e.g. it resolved in the same tick
      // the query changed) is dropped — on both the happy path AND the failure
      // path — rather than rendering stale content or clobbering the newer
      // request's loader/results with an error it no longer owns.
      if (shouldRender && !shouldRender()) return;
      if ((response.ok || (allowValidationErrors && response.status === 422)) && !response.redirected && this.isTurboStreamResponse(response)) {
        const html = await response.text();
        // The query can change while a large/slow body is being consumed after
        // headers arrived. Recheck before enqueueing Turbo, and again at its action.
        if (shouldRender && !shouldRender()) return;
        await this.renderCurrentStream(html, signal, shouldRender);
      } else if (onFailure) {
        // Anything that is not a genuine turbo stream — a non-2xx (5xx), a followed
        // auth redirect to an HTML login page (response.redirected, still ok), or a
        // wrong Content-Type — must NOT be handed to Turbo (it would render nothing
        // and leave the loader hung). Surface the unavailable state instead.
        onFailure();
      }
    } catch (error) {
      // An aborted request is a superseded search/select, not a failure: a newer
      // request already owns the region, so leave its loader/results untouched.
      // A late hard failure from a now-stale request is dropped for the same
      // reason it would be on the success path.
      if (error.name === "AbortError" || !onFailure) return;
      if (shouldRender && !shouldRender()) return;
      onFailure();
    }
  }

  async renderCurrentStream(html, signal, shouldRender) {
    const streams = [];
    const guardRender = event => {
      const render = event.detail.render;
      event.detail.render = stream => {
        if (!signal?.aborted && this.element.isConnected && (!shouldRender || shouldRender())) return render(stream);
      };
      streams.push(event.target);
    };
    // As in ajax-tabs, connection dispatches this event synchronously, but
    // Turbo performs the action after its next repaint. Keep manual-site edits
    // invalidating the request until that action has finished.
    const document = this.element.ownerDocument;
    document.addEventListener("turbo:before-stream-render", guardRender);
    try {
      window.Turbo.renderStreamMessage(html);
    } finally {
      document.removeEventListener("turbo:before-stream-render", guardRender);
    }
    await Promise.all(streams.map(stream => stream.render()));
  }

  // A genuine turbo-stream answer declares the turbo-stream Content-Type. A
  // redirected login page or any other HTML body reports a different type (or
  // none), so this guards renderStreamMessage from being fed non-stream markup.
  isTurboStreamResponse(response) {
    const contentType = response.headers?.get?.("Content-Type") || "";
    return contentType.includes("turbo-stream");
  }

  // Abort any in-flight Google search request and forget its controller so a
  // late response leaves the results region alone.
  abortSearch() {
    if (this.searchController) {
      this.searchController.abort();
      this.searchController = null;
    }
  }

  // Abort any in-flight place-details (select) request so a late response can't
  // repopulate the candidates region or its signed token after the operator has
  // moved on.
  abortSelect() {
    if (this.selectController) {
      this.selectController.abort();
      this.selectController = null;
    }
  }

  resetGoogleSelection() {
    this.clearToken();
    this.markSelectedCard(null, this.candidateCards());
    this.hideManualEntry();
    this.googleActive = false;
    this.updateSubmit();
  }

  clearToken() {
    if (!this.hasTokenTarget) return;
    this.tokenTarget.value = "";
    this.tokenTarget.disabled = true;
  }

  clearCandidates() {
    if (this.hasCandidatesTarget) this.candidatesTarget.innerHTML = "";
  }

  clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = "";
  }

  setBusy(isBusy) {
    if (!this.hasInputTarget) return;
    if (this.busyClassValue) this.inputTarget.classList.toggle(this.busyClassValue, isBusy);
    this.inputTarget.setAttribute("aria-busy", isBusy ? "true" : "false");
    if (this.hasResultsTarget) this.resultsTarget.setAttribute("aria-busy", isBusy ? "true" : "false");
  }

  // A minimal, term-free pending line (spinner + message, no card) so a slow
  // place-details lookup reads as in-progress rather than a dead click.
  showLoading(target, message) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "flex items-center gap-2 text-sm text-support-light-700";
    p.setAttribute("role", "status");
    p.setAttribute("aria-live", "polite");
    p.setAttribute("data-testid", "sca-google-places-loading");
    const spinner = document.createElement("span");
    spinner.className = "inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-core-purple-600 border-t-transparent";
    spinner.setAttribute("aria-hidden", "true");
    p.append(spinner, document.createTextNode(message));
    target.replaceChildren(p);
  }

  // Client-rendered mirror of the server's :error partials for transport
  // failures the server never got to answer. textContent keeps the (already
  // term-free) message from being interpreted as markup.
  showError(target, message, testid) {
    if (!target) return;
    const p = document.createElement("p");
    p.className = "text-sm text-alert-red-600";
    p.setAttribute("role", "alert");
    p.setAttribute("data-testid", testid);
    p.textContent = message;
    target.replaceChildren(p);
  }

  candidateCards() {
    return Array.from(this.element.querySelectorAll("[data-token]"));
  }

  placeCards() {
    return Array.from(this.element.querySelectorAll("[data-sca-place-card]"));
  }

  isSelectedCard(card) {
    return card.getAttribute("aria-selected") === "true";
  }

  // Result cards carry a checkbox in the control slot but the pick is single:
  // the active card gets the selected styling + checked box and every other
  // card in the group collapses (hidden on its list-item wrapper) until the
  // pick is cleared with activeCard = null.
  markSelectedCard(activeCard, cards, collapse = true) {
    const selected = this.selectedClassesValue.split(" ").filter(Boolean);
    const unselected = this.unselectedClassesValue.split(" ").filter(Boolean);
    cards.forEach((card) => {
      const isActive = card === activeCard;
      card.setAttribute("aria-selected", isActive ? "true" : "false");
      card.classList.remove(...(isActive ? unselected : selected));
      card.classList.add(...(isActive ? selected : unselected));
      const box = card.querySelector("input[type='checkbox']");
      if (box) box.checked = isActive;
      const row = card.closest("[role='listitem']") || card;
      row.classList.toggle("hidden", collapse && Boolean(activeCard) && !isActive);
    });
  }

  hideManualEntry() {
    if (!this.hasManualFieldsTarget) return;

    this.manualFieldsTarget.classList.add("hidden");
    this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = true; });
    this.applyUnknownState();
  }

  showManualEntry() {
    if (!this.hasManualFieldsTarget) return;

    this.manualFieldsTarget.classList.remove("hidden");
    this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = false; });
    // An identifier already marked unknown stays cleared and disabled.
    this.applyUnknownState();
    // The first identifier the operator can actually type into: never a hidden
    // "unknown" input, and never one they already said they can't supply.
    this.manualFieldsTarget.querySelector("input:not([type='hidden']):not([disabled])")?.focus();
  }

  memberId() {
    return this.element.querySelector('[name="member_id"]')?.value || "";
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
